// Crawler-facing SEO content for thin marketing pages.
//
// Why this exists: Ahrefs/GSC showed our feature & product pages were technically
// indexable but too thin (200–400 words) for Google to index or rank. This adds a
// substantial, keyword-grounded prose + FAQ block to each page via <SeoContent slug=…/>.
//
// English only on purpose: search/answer-engine crawlers don't send a locale cookie,
// so they always render the default (en) build. Each block is grounded in real Heyweek
// capabilities — do not invent features that don't exist.

export interface FaqItem {
	q: string;
	a: string;
}

export interface PageContent {
	heading: string;
	intro: string;
	body: string[];
	faq: FaqItem[];
}

export const pageContent: Record<string, PageContent> = {
	'time-tracking': {
		heading: 'Time tracking software built for real work',
		intro:
			'Heyweek is time tracking software that turns the hours you actually work into accurate, billable records — without the friction of spreadsheets or sticky notes. Start a timer with one click, log time after the fact, or track from the browser extension, mobile app, or command line, and every entry flows straight into your projects, invoices, and reports.',
		body: [
			'Most teams lose money not because they work too little, but because they never capture the work they do. A good time tracking app removes that leak. With Heyweek you assign time to clients, projects, and tasks as you go, tag entries as billable or non-billable, and see exactly where your week went. Freelancers use it to justify their rates, agencies use it to keep projects profitable, and consultants use it to bill with confidence.',
			'Because time tracking sits at the centre of Heyweek, the data you capture is never trapped. Tracked hours become professional invoices in a couple of clicks, feed live into project budgets so you spot overruns early, and roll up into reports that show utilisation across the whole team. It is time tracking that pays for itself.'
		],
		faq: [
			{
				q: 'What is the best way to track time accurately?',
				a: 'Track time as you work rather than reconstructing it later. Heyweek lets you start a one-click timer from the web app, mobile app, browser extension, or CLI, so capturing time fits into whatever tool you already have open.'
			},
			{
				q: 'Can I mark hours as billable or non-billable?',
				a: 'Yes. Every time entry can be tagged billable or non-billable and assigned to a client, project, and task, so your invoices only ever include the hours you mean to charge for.'
			},
			{
				q: 'Does the tracked time turn into invoices?',
				a: 'It does. Billable hours flow directly into Heyweek invoicing, so you can generate a professional invoice from tracked time in seconds instead of copying numbers between tools.'
			},
			{
				q: 'Is Heyweek time tracking good for teams as well as freelancers?',
				a: 'Both. Solo freelancers get a frictionless timer and clean records for freelance time tracking, while teams and agencies get utilisation reports, per-project budgets, and visibility into where everyone’s hours are going.'
			},
			{
				q: 'Does Heyweek handle time and attendance tracking?',
				a: 'Yes. Alongside project time tracking, Heyweek records when your team works, giving you simple time and attendance tracking without paying for separate software.'
			}
		]
	},

	invoicing: {
		heading: 'Invoicing software for freelancers and teams',
		intro:
			'Heyweek is invoicing software for freelancers, consultants, and agencies who would rather get paid than chase payments. Turn tracked time and expenses into a professional, branded invoice in seconds, send it for online payment, and let automated reminders do the awkward follow-up for you.',
		body: [
			'Late payment is one of the biggest threats to any independent business, and most of it comes down to slow, manual invoicing. Heyweek closes that gap. Because your billable hours and expenses already live in the platform, online invoicing becomes a one-click step: select the work, apply your template, and send. Clients can pay by card or bank transfer, and you always know who owes you what.',
			'Smart reminders mean you never have to send the “just checking in” email again — Heyweek nudges clients before a due date and follows up firmly on overdue invoices. Real-time payment tracking shows paid, pending, and overdue at a glance, so freelance invoicing stops being a monthly chore and starts protecting your cash flow.'
		],
		faq: [
			{
				q: 'How do I create an invoice from tracked time?',
				a: 'Select the billable time entries and expenses you want to bill, choose an invoice template, and Heyweek generates a professional invoice instantly — no manual copying from a timesheet.'
			},
			{
				q: 'Can clients pay invoices online?',
				a: 'Yes. Invoices include online payment options such as card and bank transfer, which is one of the most reliable ways to get paid faster.'
			},
			{
				q: 'Will Heyweek chase late payments for me?',
				a: 'Automated reminders send a polite nudge before the due date and firmer follow-ups once an invoice is overdue, so you stop chasing payments manually.'
			},
			{
				q: 'Is this suitable for freelance invoicing specifically?',
				a: 'Absolutely — Heyweek is built for freelancers who need professional, tax-friendly invoices without accounting software, as well as agencies billing many clients at once.'
			},
			{
				q: 'Can I automate my invoicing?',
				a: 'Yes. Invoice automation in Heyweek turns tracked time into invoices and sends payment reminders automatically, so the whole billing cycle runs with minimal manual work.'
			}
		]
	},

	'project-management': {
		heading: 'Project management software that keeps work profitable',
		intro:
			'Heyweek is project management software that connects the plan to the money. Organise work into projects, break it down into tasks and boards, and track the time and costs against each one — so you always know whether a project is on schedule and on budget, not just busy.',
		body: [
			'Plenty of project management tools show you what needs doing. Few show you what it actually costs. Heyweek does both: every task can hold estimates, assignees, and due dates, while tracked time and expenses roll up into a live project budget. That means scope creep and over-servicing surface early, while you can still do something about them.',
			'Whether you run client projects in an agency, manage consulting engagements, or coordinate an internal team, Heyweek gives you one place for tasks, kanban boards, files, notes, and communication tied to each project. No more stitching together five apps to answer a simple question: “where is this, and are we making money on it?”'
		],
		faq: [
			{
				q: 'What makes Heyweek different from other project management software?',
				a: 'Heyweek links project planning directly to time tracking, budgets, and invoicing, so you manage delivery and profitability in one tool instead of guessing whether projects are making money.'
			},
			{
				q: 'Can I see if a project is over budget?',
				a: 'Yes. Tracked hours and expenses roll up into a live project budget, so you can spot overruns while there is still time to act.'
			},
			{
				q: 'Does it support both tasks and kanban boards?',
				a: 'It does. You can organise project work as task lists or visual kanban boards, whichever fits how your team likes to work.'
			},
			{
				q: 'Is Heyweek good for client and agency projects?',
				a: 'Yes — agencies and consultants use Heyweek to run multiple client projects, keep files and communication in context, and bill the work that gets done.'
			}
		]
	},

	todos: {
		heading: 'A to-do list app that lives next to your work',
		intro:
			'Heyweek gives you a fast, flexible to-do list right inside the workspace where your real work already happens. Capture everything you need to do, organise it into lists, set priorities and due dates, break big items into subtasks, and track the time each one takes — without bolting on yet another standalone app.',
		body: [
			'A good to-do list is more than a checklist. Heyweek todos carry the detail that helps you actually finish: a priority from low to critical, a due date, subtasks for the bigger jobs, and optional time estimates so you can plan a realistic day. Keep personal todos to yourself or share lists with your team, sort by priority or due date, and postpone what can wait so today stays focused.',
			'Because your todos sit in the same place as your projects, tasks, time tracking, and notes, the small stuff never drifts away from the work it belongs to. Log time against a todo and it sharpens your next estimate; finish one and your day genuinely moves forward. It is the difference between a to-do app you forget about and a list that keeps up with you.'
		],
		faq: [
			{
				q: 'What is the difference between todos and tasks in Heyweek?',
				a: 'Todos are a lightweight personal and shared to-do list — quick to capture, organised into lists, with priorities, subtasks, and due dates. Tasks are board-based cards that move through Kanban stages and tie into projects and clients for team delivery. Use todos for your day-to-day list and tasks for project work.'
			},
			{
				q: 'Can I organise my todos into separate lists?',
				a: 'Yes. Group todos into as many lists as you like — for example work, a specific client, or personal — and filter to one list or see everything in a single view.'
			},
			{
				q: 'Can I add subtasks and priorities?',
				a: 'You can. Break any todo into subtasks and check them off as you go, and set a priority from low to critical so the most important work rises to the top.'
			},
			{
				q: 'Can I track time on my todos?',
				a: 'Yes. Estimate a todo in minutes, hours, or days, then log the time you actually spend, so your estimates get more accurate and your effort is captured alongside the rest of your work.'
			}
		]
	},

	pulse: {
		heading: 'A team feed that replaces the status meeting',
		intro:
			'Pulse is the social feed built into Heyweek — the place your team shares quick updates, wins, and blockers, runs polls, and reacts to each other’s posts. It keeps everyone in the loop and connected without scheduling another status meeting or losing announcements in a busy chat channel.',
		body: [
			'Teams stay aligned when information is easy to share and easy to find. With Pulse, anyone can post a short update with text, images, or GIFs, ask the team a question with a quick poll, and let people react and comment in seconds. Follow specific teammates to tailor your feed, or scan everything happening across the organisation — so remote and in-office people alike know what’s going on day to day.',
			'Because Pulse lives inside the same workspace as your projects, tasks, and time tracking, updates stay close to the work they’re about. You can scope a post to a team, project, or client when it’s only relevant to some people, keeping the main feed clear. It’s an informal, low-effort way to keep a company connected — the heartbeat of the team, in one feed.'
		],
		faq: [
			{
				q: 'What is Pulse in Heyweek?',
				a: 'Pulse is a team activity feed where people share updates, wins, and blockers, create polls, and react and comment on posts. It gives your team a single place to stay connected without extra meetings.'
			},
			{
				q: 'Can I run polls in Pulse?',
				a: 'Yes. Create a poll with multiple options to gather your team’s opinion quickly, see the results as people vote, and make decisions without booking a meeting.'
			},
			{
				q: 'Can I control who sees a post?',
				a: 'You can. Posts can be shared with everyone or scoped to a specific team, project, or client, so each update reaches the people it’s relevant to.'
			},
			{
				q: 'How is Pulse different from chat or messages?',
				a: 'Chat is for back-and-forth conversation, while Pulse is a feed for broadcast-style updates, announcements, and polls that everyone can catch up on in their own time — so important news doesn’t scroll away in a busy channel.'
			}
		]
	},

	tasks: {
		heading: 'Task management software with built-in Kanban boards',
		intro:
			'Heyweek is task management software that keeps the day-to-day moving. Create tasks, assign owners, set priorities and due dates, view everything as a list or on a Kanban board, and track the time each one takes — all connected to the projects and clients the work belongs to.',
		body: [
			'A task list only helps if it reflects reality. Heyweek tasks carry the context that matters: who is responsible, when it is due, how important it is, and how much time has gone into it. You can view tasks as a list or on a board, filter by assignee or project, and watch work flow from to-do to done without losing the thread.',
			'Because tasks live inside the same platform as your time tracking, invoicing, and reports, completed work doesn’t disappear into a void — it becomes billable hours, project progress, and data you can learn from. That tight loop is what separates real task management software from a glorified checklist.'
		],
		faq: [
			{
				q: 'Can I assign tasks and set priorities?',
				a: 'Yes. Each task supports an owner, priority, due date, and project, so everyone knows what to do next and why it matters.'
			},
			{
				q: 'Can I track time against individual tasks?',
				a: 'You can. Time entries attach to specific tasks, which makes estimates more accurate over time and turns completed work into billable hours.'
			},
			{
				q: 'Is there a Kanban board view as well as a list?',
				a: 'Yes. Heyweek offers both list and Kanban board views. Customise board columns to match your workflow — for example backlog, in progress, review, and done — and drag cards between them as work advances, so you can spot bottlenecks at a glance.'
			},
			{
				q: 'How is this different from a standalone to-do app?',
				a: 'Heyweek connects tasks to projects, clients, time tracking, and invoicing, so finishing a task actually advances the work and the billing — not just a checklist.'
			}
		]
	},

	clients: {
		heading: 'Client management software for service businesses',
		intro:
			'Heyweek is client management software that keeps everything about a client in one place — projects, tracked time, invoices, files, and conversations. Instead of hunting through email and folders, you get a single, organised view of every relationship and exactly where it stands.',
		body: [
			'For freelancers, consultants, and agencies, the client is the unit that matters. Heyweek is built around that. Each client record connects to the projects you deliver, the hours you track, the invoices you send, and the documents you share, so onboarding a new client or answering “what have we done for them?” takes seconds, not an afternoon of digging.',
			'Good client management software also protects the commercial relationship. With Heyweek you can see profitability per client, keep billing accurate and on time, and give clients a clear, professional experience from first project to final invoice. Strong relationships are easier to keep when nothing falls through the cracks.'
		],
		faq: [
			{
				q: 'What does client management in Heyweek include?',
				a: 'Each client connects to their projects, tracked time, invoices, files, and communication, giving you one organised view of the entire relationship.'
			},
			{
				q: 'Can I see how profitable each client is?',
				a: 'Yes. Because time, costs, and invoices are linked to the client, you can see which relationships are actually profitable and which need re-pricing.'
			},
			{
				q: 'Is this suitable for agencies with many clients?',
				a: 'It is. Heyweek is designed for service businesses juggling multiple clients and projects at once, keeping each one’s work and billing cleanly separated.'
			},
			{
				q: 'Do I need separate CRM and billing tools?',
				a: 'No — Heyweek combines client records, project delivery, and invoicing, so you don’t have to glue a CRM to separate accounting software.'
			}
		]
	},

	finance: {
		heading: 'Cash flow and expense tracking software in one place',
		intro:
			'Heyweek brings cash flow management and expense tracking software together so you always know where your business stands. Log expenses, watch money in and out, and turn billable costs into invoices — all connected to the projects and clients they belong to.',
		body: [
			'Cash flow, not profit on paper, is what keeps a business alive. Heyweek gives you a real-time picture of income and outgoings, so you can see what’s coming in from outstanding invoices and what’s going out in costs. That foresight is the difference between confidently taking on a new project and nervously hoping the numbers work out.',
			'As expense tracking software, Heyweek makes capturing costs effortless and meaningful: record an expense, attach it to a client or project, and decide whether to pass it on. Billable expenses flow straight into invoices, and everything rolls up into clear financial reporting — no separate spreadsheet, no month-end surprises.'
		],
		faq: [
			{
				q: 'How does Heyweek help with cash flow management?',
				a: 'It shows income and outgoings in real time, including what’s owed on outstanding invoices, so you can predict cash flow instead of reacting to it.'
			},
			{
				q: 'Can I track and rebill expenses?',
				a: 'Yes. Record expenses, attach them to a project or client, mark them billable, and they flow directly onto the relevant invoice.'
			},
			{
				q: 'Do I still need separate expense tracking software?',
				a: 'No — expense tracking is built in and linked to your projects, clients, and invoicing, so costs and billing stay in one connected system.'
			},
			{
				q: 'Does finance data connect to my projects?',
				a: 'It does. Expenses and billable costs roll up into project budgets and financial reports, so you see profitability per project and overall.'
			},
			{
				q: 'Is Heyweek time and expense tracking software?',
				a: 'Yes. Heyweek is time and expense tracking software in one — it captures both billable hours and costs against each project, so nothing billable slips through and rebilling is automatic.'
			}
		]
	},

	reports: {
		heading: 'Reporting and timesheet software for clear decisions',
		intro:
			'Heyweek reporting and timesheet software turns your tracked work into answers. See where time goes, which projects and clients are profitable, and how your team is utilised — in clear reports you can act on, not raw data you have to wrestle.',
		body: [
			'Timesheets are only useful if they tell you something. Heyweek rolls up every tracked hour into reports on billable versus non-billable time, project budgets, client profitability, and team utilisation. Export a clean timesheet for payroll or a client, or drill into a project to understand exactly where the hours went.',
			'Because the underlying data is captured naturally as people work, your reports stay accurate without anyone filling in a spreadsheet at the end of the week. That makes Heyweek reporting trustworthy enough to base real decisions on — pricing, hiring, and which work to take on next.'
		],
		faq: [
			{
				q: 'What can I report on in Heyweek?',
				a: 'Billable and non-billable time, project budgets and overruns, client profitability, and team utilisation — all built from the time and costs you already track.'
			},
			{
				q: 'Can I export timesheets?',
				a: 'Yes. You can produce clean timesheets to share with clients or use for payroll, generated automatically from tracked time.'
			},
			{
				q: 'Are reports accurate without manual data entry?',
				a: 'They are, because data is captured as people work rather than reconstructed later, so reports reflect what actually happened.'
			},
			{
				q: 'Can I see profitability per client and project?',
				a: 'Yes — reporting ties time, costs, and invoices together so you can see which clients and projects make money and which don’t.'
			}
		]
	},

	communication: {
		heading: 'A team communication app that keeps work in context',
		intro:
			'Heyweek is a team communication app where conversations live next to the work they’re about. Discuss tasks, projects, and clients without scattering context across email threads and separate chat tools — so decisions stay connected to what’s actually being delivered.',
		body: [
			'The problem with most team chat isn’t the chat — it’s that it’s disconnected from the work. A decision made in a message thread is useless if nobody can find it later. Heyweek keeps communication tied to projects, tasks, and clients, so the conversation and the work are never more than a click apart.',
			'For distributed and hybrid teams especially, that context is everything. Instead of asking “which tool was that in?”, your team has one place where messages, files, tasks, and time all reinforce each other. Communication becomes part of getting work done, not a separate inbox to manage.'
		],
		faq: [
			{
				q: 'How is Heyweek different from a standalone chat app?',
				a: 'Conversations are attached to the projects, tasks, and clients they’re about, so context and decisions stay connected to the work instead of getting lost in a separate tool.'
			},
			{
				q: 'Is it suitable for remote and hybrid teams?',
				a: 'Yes. Keeping messages, files, and tasks in one place is especially valuable for distributed teams who can’t rely on hallway conversations.'
			},
			{
				q: 'Can I discuss a specific task or project?',
				a: 'You can comment in the context of individual tasks, projects, and clients, so everyone involved sees the relevant discussion.'
			},
			{
				q: 'Does this replace email for internal work?',
				a: 'For project and client work, yes — it keeps internal communication organised around the work rather than buried in inboxes.'
			}
		]
	},

	files: {
		heading: 'File sharing that stays attached to your work',
		intro:
			'Heyweek keeps your files where the work is. Share documents, briefs, and deliverables in the context of the project, task, or client they belong to — so the right file is always a click away instead of lost in a chat thread or a sprawling drive.',
		body: [
			'Files lose their value the moment they’re separated from context. A brief in a generic folder is just a document; a brief attached to its project is part of the work. Heyweek lets you store and share files against projects, tasks, and clients, so anyone on the team can find what they need without asking where it lives.',
			'Because file sharing sits inside the same platform as your tasks, time tracking, and communication, collaboration stays tidy. Clients and teammates work from the same up-to-date documents, and nothing important gets stranded in someone’s inbox.'
		],
		faq: [
			{
				q: 'Where are files stored in Heyweek?',
				a: 'Files attach to the relevant project, task, or client, so they live in the same context as the work rather than in a disconnected folder.'
			},
			{
				q: 'Can I share files with clients and teammates?',
				a: 'Yes. Documents and deliverables can be shared with the people involved in a project, so everyone works from the same up-to-date version.'
			},
			{
				q: 'Does this replace a separate file drive?',
				a: 'For work-related documents it keeps everything in context, so you spend less time hunting across drives and chat threads.'
			},
			{
				q: 'Are files connected to tasks and projects?',
				a: 'They are — attaching files to tasks and projects means the right document is always next to the work it supports.'
			}
		]
	},

	people: {
		heading: 'Team and people management in one workspace',
		intro:
			'Heyweek helps you manage the people behind the work. Organise your team, set roles and permissions, see who’s working on what, and keep capacity in view — so you can staff projects well and avoid overloading anyone.',
		body: [
			'Running a team is a scheduling problem as much as a management one. Heyweek shows you who’s assigned where, how utilised each person is, and where there’s capacity for new work. That makes it far easier to plan projects realistically and spot burnout risk before it becomes a problem.',
			'Roles and permissions keep the workspace appropriate for everyone — owners, managers, team members, and clients each see what they should. Combined with time tracking and reporting, people management in Heyweek connects your team to the actual work and the results it produces.'
		],
		faq: [
			{
				q: 'Can I set roles and permissions for my team?',
				a: 'Yes. Owners, managers, members, and clients get appropriate access, so everyone sees what they need and nothing they shouldn’t.'
			},
			{
				q: 'Can I see who is working on what?',
				a: 'You can. Heyweek shows assignments and utilisation across the team, making it easier to staff projects and balance workload.'
			},
			{
				q: 'Does this help with team scheduling?',
				a: 'It does — seeing capacity and current assignments in one place makes planning who works on which project far more realistic.'
			},
			{
				q: 'Is people management connected to time and reports?',
				a: 'Yes. Team data ties into time tracking and reporting, so you can see utilisation and profitability per person and project.'
			}
		]
	},

	absence: {
		heading: 'Absence management software without the spreadsheets',
		intro:
			'Heyweek is absence management software that makes leave simple to request, approve, and plan around. Track vacation, sick days, and time off in one place, see who’s away at a glance, and keep project planning realistic when people are out.',
		body: [
			'Absence tracking by spreadsheet is error-prone and invisible — and nothing derails a project plan faster than discovering a key person is on leave the week you needed them. Heyweek keeps leave requests, approvals, and balances in one place, so absences are visible to everyone who needs to plan around them.',
			'Because absence connects to the rest of the workspace, you’re not planning capacity in one tool and tracking leave in another. A request goes in, the manager approves it in a couple of clicks, the person’s remaining balance updates, and that week immediately shows them as away on the team calendar — so when you schedule the next sprint, you can already see that two people are out on the Thursday. Project schedules reflect reality rather than wishful thinking.'
		],
		faq: [
			{
				q: 'How does absence tracking work in Heyweek?',
				a: 'Team members request time off, managers approve it, and balances update automatically — replacing fragile spreadsheets with one clear, shared record.'
			},
			{
				q: 'Can I see who is away at a glance?',
				a: 'Yes. Upcoming absences are visible to the team, so everyone can plan projects and cover around people who are out.'
			},
			{
				q: 'Does absence affect project planning?',
				a: 'It does — because absence lives in the same workspace as your projects and people, schedules and capacity reflect who’s actually available.'
			},
			{
				q: 'What types of leave can I track?',
				a: 'You can track vacation, sick days, and other time off, keeping balances and approvals organised in one place.'
			}
		]
	},

	notes: {
		heading: 'Notes and documentation that live with your work',
		intro:
			'Heyweek notes keep knowledge where you’ll actually use it. Capture meeting notes, briefs, and documentation in the context of the project, client, or task they relate to — so important details aren’t scattered across apps and forgotten.',
		body: [
			'The best note is the one you can find when you need it. Heyweek lets you write and organise notes against the work they belong to, turning scattered thoughts into a searchable, shared knowledge base for your team. Onboarding, decisions, and client details stay put instead of disappearing into someone’s personal app.',
			'Because notes sit alongside tasks, files, time tracking, and communication, documentation becomes part of the workflow rather than an afterthought. Your team builds shared context as it works, and nobody has to reconstruct what was decided three weeks ago.'
		],
		faq: [
			{
				q: 'Can I attach notes to projects and clients?',
				a: 'Yes. Notes live in the context of the relevant project, client, or task, so the information is right where the work happens.'
			},
			{
				q: 'Is this a shared knowledge base?',
				a: 'It can be — notes are shared with your team, turning meeting notes and documentation into searchable shared context.'
			},
			{
				q: 'Do notes connect to the rest of my work?',
				a: 'They do. Notes sit alongside tasks, files, and communication, so documentation is part of the workflow rather than a separate tool.'
			},
			{
				q: 'Is Heyweek notes good for client work?',
				a: 'Yes — keeping client details and decisions attached to the client record means anyone on the team can pick up where others left off.'
			}
		]
	},

	'team-support': {
		heading: 'One workspace for the whole team to collaborate',
		intro:
			'Heyweek gives teams a single place to work together — projects, tasks, time, files, notes, and communication, all connected. Instead of switching between half a dozen apps, your team collaborates in one workspace where everything stays in context.',
		body: [
			'Tool sprawl quietly taxes every team: time lost switching apps, context lost between them, and decisions lost in the gaps. Heyweek replaces that with an all-in-one workspace where the parts reinforce each other. Open a single task and you can see the hours already logged against it, the brief attached to it, the client it belongs to, and the thread where the scope was agreed — without opening four other tabs. That is the difference in practice: collaboration is fast, and nothing falls through the cracks between tools.',
			'Whether you’re a small studio or a growing agency, having delivery, communication, and billing in one place means everyone is working from the same truth. New team members get up to speed faster, and the work — not the tooling — gets the attention.'
		],
		faq: [
			{
				q: 'What does Heyweek bring together for teams?',
				a: 'Projects, tasks, time tracking, files, notes, communication, and invoicing live in one workspace, so teams collaborate without juggling separate tools.'
			},
			{
				q: 'Why use one platform instead of several apps?',
				a: 'Keeping work in one place removes the time and context lost to switching tools, and means everyone works from the same up-to-date information.'
			},
			{
				q: 'Does it help new team members get up to speed?',
				a: 'Yes. Because work, decisions, and documentation are all in context, new members can see what’s happening and why without lengthy handovers.'
			},
			{
				q: 'Is Heyweek suitable for growing teams and agencies?',
				a: 'It is — Heyweek scales from solo freelancers to agencies, keeping delivery, collaboration, and billing connected as the team grows.'
			}
		]
	},

	'products-web': {
		heading: 'Online time tracking and work management in your browser',
		intro:
			'The Heyweek web app is online time tracking and work management that runs anywhere you have a browser — no install, always up to date. Track time, manage projects and tasks, invoice clients, and see your finances from one fast, secure workspace on any computer.',
		body: [
			'A web-based platform means your work goes where you do. Sign in from the office, a client site, or home, and everything is exactly as you left it — the same projects, the same tracked time, the same invoices. Because there’s nothing to install or update, your whole team is always on the latest version, working from a single source of truth.',
			'The web app is the full Heyweek experience: one-click time tracking, kanban boards and tasks, invoicing and payments, cash flow and expenses, client management, files, notes, and team communication. It pairs with the mobile app, browser extension, and CLI, so you can capture and manage work from whichever surface is closest to hand.'
		],
		faq: [
			{
				q: 'Do I need to install anything to use Heyweek?',
				a: 'No. The web app runs in any modern browser, so you can track time and manage work without downloading or updating software.'
			},
			{
				q: 'Can I access my data from any computer?',
				a: 'Yes. Everything is stored in your secure workspace, so your projects, time, and invoices are available from any device you sign in on.'
			},
			{
				q: 'Does the web app include all features?',
				a: 'It’s the complete Heyweek experience — time tracking, projects, tasks, invoicing, finance, clients, files, notes, and communication in one place.'
			},
			{
				q: 'Does it work alongside the mobile app and extension?',
				a: 'It does. The web app, mobile app, browser extension, and CLI all share the same workspace, so your work stays in sync everywhere.'
			}
		]
	},

	'products-mobile': {
		heading: 'A time tracking app for work on the move',
		intro:
			'The Heyweek mobile time tracking app puts your whole workspace in your pocket. Start and stop timers, log hours, check tasks, and keep projects moving from your phone — so time gets captured the moment work happens, not hours later from memory.',
		body: [
			'The most accurate time tracking is the time you record as you work. A mobile app makes that possible away from your desk: track a client call, log billable travel, or update a task between meetings. Entries sync instantly with the web app, so the hours you capture on your phone flow straight into projects, reports, and invoices.',
			'Beyond the timer, the mobile app keeps you connected to what matters — your tasks, your projects, and your team. Notifications keep you in the loop, offline support means a patchy signal won’t lose your work, and everything stays in sync the moment you’re back online.'
		],
		faq: [
			{
				q: 'Can I track time from my phone?',
				a: 'Yes. The mobile app lets you start and stop timers and log hours on the go, so time is captured when work actually happens.'
			},
			{
				q: 'Does mobile data sync with the web app?',
				a: 'It does. Time, tasks, and updates sync instantly across the mobile and web apps, so everything stays consistent.'
			},
			{
				q: 'Does the app work offline?',
				a: 'Offline support means you can keep tracking and working without a connection, and your data syncs as soon as you’re back online.'
			},
			{
				q: 'Is the mobile app available for iOS and Android?',
				a: 'Yes — Heyweek’s mobile experience covers both iOS and Android so your whole team can track time from any phone.'
			}
		]
	},

	'products-extensions': {
		heading: 'A browser extension time tracker for your workflow',
		intro:
			'The Heyweek browser extension brings time tracking to wherever you already work. Start a timer, log hours, and link time to the right project and task without leaving the tab you’re in — capturing billable work with a single click instead of switching apps.',
		body: [
			'Context switching is the enemy of accurate time tracking. Every trip to a separate app is a chance to forget what you were doing. The browser extension removes that friction: a one-click timer sits right in your toolbar, so you can track time against the task at hand and get back to work in seconds.',
			'Everything the extension captures flows into your Heyweek workspace, where it becomes billable hours, project progress, and reportable data. Paired with the web app, mobile app, and CLI, the extension means there’s never a reason for tracked time to slip through the cracks.'
		],
		faq: [
			{
				q: 'What does the Heyweek browser extension do?',
				a: 'It lets you start and stop timers and log time directly from your browser toolbar, so you can track billable work without switching apps.'
			},
			{
				q: 'Does extension time link to projects and tasks?',
				a: 'Yes. Time tracked in the extension attaches to the right project and task and syncs to your workspace automatically.'
			},
			{
				q: 'Which browsers are supported?',
				a: 'The extension is built for the major modern browsers, so most teams can add it to the browser they already use.'
			},
			{
				q: 'Why use the extension instead of the web app?',
				a: 'The extension removes context switching — you track time against what you’re doing without leaving the page, which makes capture far more reliable.'
			}
		]
	},

	'products-cli': {
		heading: 'Command-line time tracking for developers',
		intro:
			'The Heyweek CLI is command-line time tracking built for people who live in the terminal. Start timers, log work, manage projects and clients, and script your workflow without ever reaching for the mouse — all syncing with the same Heyweek workspace as the web and mobile apps.',
		body: [
			'For developers and power users, the fastest interface is the keyboard. The Heyweek CLI lets you track time, switch projects, and log work with a few keystrokes, then automate the repetitive parts with scripts in shell, Node.js, or Python. Time tracking stops being a chore you remember to do and becomes part of your existing terminal workflow.',
			'Because the CLI talks to the same workspace as everything else, the hours you track from the command line become billable time, project progress, and reportable data automatically. Install it via your usual package manager, wire it into your scripts and CI, and keep tracking without breaking flow.'
		],
		faq: [
			{
				q: 'What can I do with the Heyweek CLI?',
				a: 'Start and stop timers, log work, and manage projects and clients straight from the terminal, with everything syncing to your Heyweek workspace.'
			},
			{
				q: 'Can I automate time tracking with scripts?',
				a: 'Yes. The CLI is scriptable in shell, Node.js, and Python, so you can automate repetitive tracking and wire it into your existing workflows.'
			},
			{
				q: 'How do I install the CLI?',
				a: 'It installs through common package managers across macOS, Windows, and Linux, so you can add it to your environment in seconds.'
			},
			{
				q: 'Does CLI-tracked time appear in invoices and reports?',
				a: 'It does — time tracked from the command line flows into the same projects, invoices, and reports as the rest of Heyweek.'
			}
		]
	},

	integrations: {
		heading: 'Heyweek integrations connect your workflow',
		intro:
			'Heyweek integrations connect your time tracking and project work to the tools you already use. Instead of becoming another island of data, Heyweek fits into your stack — so your hours, projects, and invoices flow to where you need them without manual copying.',
		body: [
			'Software earns its place when it works with everything else, not against it. Heyweek is designed to connect with the apps around it, and its scriptable CLI and structured data make it straightforward to move information between tools. That means less double entry, fewer mistakes, and a workflow that holds together instead of fragmenting across a dozen tabs.',
			'Because so much already lives inside Heyweek — time tracking, projects, tasks, invoicing, finance, clients, files, and communication — you’ll need fewer integrations than with a single-purpose tool. The result is an all-in-one workspace that plays well with the rest of your stack rather than fighting it.'
		],
		faq: [
			{
				q: 'How does Heyweek connect with other tools?',
				a: 'Heyweek is built to fit into your stack, with a scriptable CLI and structured data that make it straightforward to move time, project, and invoice information between tools.'
			},
			{
				q: 'Do I need lots of integrations to get value?',
				a: 'Less than with single-purpose tools — because time tracking, projects, invoicing, finance, and communication are already built in, much of what you’d normally integrate is native.'
			},
			{
				q: 'Can developers automate workflows?',
				a: 'Yes. The CLI is scriptable in shell, Node.js, and Python, so technical teams can automate how data flows in and out of Heyweek.'
			},
			{
				q: 'Will my tracked time reach my other systems?',
				a: 'Heyweek’s structured data and CLI make it possible to push hours, projects, and invoices to the other systems your business relies on.'
			}
		]
	},

	about: {
		heading: 'About Heyweek',
		intro:
			'Heyweek is all-in-one professional services automation (PSA) software that lets freelancers and growing teams run their entire business — time, projects, clients, invoicing, and finances — from one connected workspace. It brings time tracking, project management, invoicing, finance, client management, and team communication together, so independent professionals spend less time wrangling tools and more time doing the work that pays.',
		body: [
			'Most people who work for themselves end up stitching together a timer, a project app, an invoicing tool, a spreadsheet for cash flow, and a chat app — then lose hours every week moving data between them. We built Heyweek because that fragmentation is expensive, error-prone, and exhausting. One connected workspace means your tracked time becomes invoices, your projects stay on budget, and nothing falls through the gaps between tools.',
			'Heyweek is built for the realities of modern, often remote work: track time from the web, mobile, browser, or command line; manage projects and people; bill clients professionally; and keep an eye on the numbers that keep a business healthy. Whether you’re a solo freelancer or a growing agency, our goal is the same — give you a clearer, calmer way to run your work.'
		],
		faq: [
			{
				q: 'Who is Heyweek for?',
				a: 'Freelancers, consultants, and teams or agencies who want to run time tracking, projects, invoicing, finance, and communication from one connected workspace.'
			},
			{
				q: 'What does Heyweek do?',
				a: 'It combines time tracking, project and task management, kanban boards, invoicing and payments, cash flow and expenses, client management, files, notes, and team communication in a single platform.'
			},
			{
				q: 'How is Heyweek different from using several separate tools?',
				a: 'Because everything is connected, your tracked time turns into invoices, projects stay on budget, and you stop losing hours moving data between disconnected apps.'
			},
			{
				q: 'Can I use Heyweek as a solo freelancer and as a team?',
				a: 'Yes — Heyweek scales from one person to a whole agency, with the same connected workspace growing alongside your business.'
			}
		]
	},

	features: {
		heading: 'All-in-one business management software',
		intro:
			'Heyweek is all-in-one business management software for freelancers, consultants, and teams. Instead of paying for and switching between a timer, a project tool, an invoicing app, a finance spreadsheet, and a chat tool, you get every feature in one connected workspace — so the work, the people, and the money all live in the same place.',
		body: [
			'Each Heyweek feature is useful on its own, but the real value is how they connect. Time tracking feeds invoices. Tasks and boards roll into project budgets. Expenses and payments shape your cash flow. Clients tie it all together. That connection is what a stack of separate single-purpose apps can never give you, no matter how good each one is.',
			'The platform covers the full span of running a service business: time tracking, project management, tasks and kanban boards, invoicing and payments, finance and expenses, client management, team communication, file sharing, notes, people and absence management, and reporting — available across web, mobile, browser extension, and command line. Explore each feature to see how it fits your workflow.'
		],
		faq: [
			{
				q: 'What features does Heyweek include?',
				a: 'Time tracking, project management, tasks and kanban boards, invoicing and payments, finance and expenses, client management, team communication, files, notes, people and absence management, and reporting — all in one platform.'
			},
			{
				q: 'Why choose one platform over separate best-in-class apps?',
				a: 'Because connected features beat disconnected ones: your time becomes invoices, your tasks become budgets, and you stop losing hours moving data between tools that don’t talk to each other.'
			},
			{
				q: 'Is Heyweek suitable for both freelancers and teams?',
				a: 'Yes — the same connected workspace works for solo freelancers and scales up to consultants and agencies.'
			},
			{
				q: 'Where can I use Heyweek?',
				a: 'Across the web app, mobile app, browser extension, and command-line tool, all sharing the same workspace.'
			}
		]
	},

	customers: {
		heading: 'Trusted by freelancers, consultants, and teams',
		intro:
			'Real teams and independent professionals use Heyweek to track time, manage projects, invoice clients, and run their businesses with less friction. Their stories show what changes when your work, your clients, and your billing finally live in one connected workspace instead of a scatter of disconnected tools.',
		body: [
			'The pattern is consistent across freelancers, consultants, and agencies: less time lost to admin, more billable hours actually captured, and invoices that go out on time and get paid faster. When time tracking, projects, and invoicing share the same data, the busywork that usually eats into evenings simply disappears.',
			'From solo designers and developers to growing studios, customers choose Heyweek because it replaces five tools with one and gives them a clear view of where their time and money go. Read their stories to see how teams like yours put the platform to work.'
		],
		faq: [
			{
				q: 'Who uses Heyweek?',
				a: 'Freelancers, consultants, and teams or agencies across creative, technical, and professional services use Heyweek to run time tracking, projects, and billing in one place.'
			},
			{
				q: 'What results do customers see?',
				a: 'Common outcomes include capturing more billable time, spending less time on admin, and getting invoices out and paid faster thanks to connected time tracking and invoicing.'
			},
			{
				q: 'Can Heyweek work for a business like mine?',
				a: 'If you track time, run projects, and invoice clients — as a freelancer or a team — Heyweek is built for exactly that, and scales as you grow.'
			},
			{
				q: 'Where can I read customer stories?',
				a: 'Browse the customer stories on this page to see how real teams and freelancers use Heyweek day to day.'
			}
		]
	}
};
